import { useState, useEffect, useCallback } from "react";
import * as api from "../../apiService";
import { useToast } from "../../components/Toast";

const STATUS_BADGE = {
  CUSTOMER: { bg:"#eff6ff", color:"#1d4ed8", border:"#bfdbfe" },
  PROSPECT: { bg:"#fefce8", color:"#854d0e", border:"#fde047" },
  INACTIVE: { bg:"#f9fafb", color:"#6b7280", border:"#e5e7eb" },
};

function fmt(n) {
  if (n == null) return "—";
  return Number(n).toLocaleString("en-AE", { minimumFractionDigits:2, maximumFractionDigits:2 });
}

const COMPANY_ACCENT = { AMI:"#1d4ed8", AMT:"#0d9488" };

const DATE_TYPES = ["BIRTHDAY","ANNIVERSARY","FESTIVAL","OTHER"];
const MONTH_NAMES = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];

// ── Small helpers ─────────────────────────────────────────────────────────────

function SectionHeader({ title, action }) {
  return (
    <div style={{ display:"flex", alignItems:"center", gap:12, marginBottom:12 }}>
      <span style={{ fontWeight:700, fontSize:13 }}>{title}</span>
      {action}
    </div>
  );
}

function EmptyNote({ text }) {
  return <div style={{ color:"#9ca3af", fontSize:13, padding:"8px 0" }}>{text}</div>;
}

function InlineErr({ msg }) {
  if (!msg) return null;
  return <div className="alert-warn" style={{ padding:"6px 10px", marginBottom:10, fontSize:12 }}>{msg}</div>;
}

// ── Account details panel (read-only) ────────────────────────────────────────

function fmtDate(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  return d.toLocaleDateString("en-AE", { day:"2-digit", month:"short", year:"numeric" });
}

function AccountDetailsPanel({ account }) {
  const tlExpiry = account.tradeLicenseExpiry;
  const tlDays   = tlExpiry
    ? Math.ceil((new Date(tlExpiry) - new Date()) / 86400000)
    : null;
  const tlColor  = tlDays == null ? null
    : tlDays < 0  ? "#b91c1c"
    : tlDays < 30 ? "#ea580c"
    : tlDays < 90 ? "#ca8a04"
    : "#15803d";

  const tallyLimit = (account.ledgers ?? []).reduce(
    (s, l) => s + (Number(l.tallyCreditLimit) || 0), 0);
  const tallyPeriod = [...new Set(
    (account.ledgers ?? []).map(l => l.tallyCreditPeriod).filter(Boolean)
  )].join(" · ");

  const fields = [
    { label:"Segment",       value: account.segment },
    { label:"VAT TRN",       value: account.vatTrn },
    { label:"Assigned To",   value: account.assignedTo },
    { label:"Credit Limit",  value: tallyLimit > 0 ? `AED ${fmt(tallyLimit)}` : null },
    { label:"Credit Days",   value: tallyPeriod || null },
  ];

  return (
    <div style={{ marginBottom:24 }}>
      <div style={{ fontWeight:700, fontSize:13, marginBottom:12 }}>Account Details</div>
      <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fit, minmax(175px, 1fr))", gap:10, marginBottom:12 }}>
        {fields.map(({ label, value, warn }) => (
          <div key={label} style={{
            background:"#f9fafb", border:"1px solid #e5e7eb",
            borderRadius:8, padding:"10px 14px",
          }}>
            <div style={{ fontSize:12, color:"#6b7280", marginBottom:4 }}>{label}</div>
            <div style={{ fontSize:15, fontWeight:600, color: value && !warn ? "#111827" : "#9ca3af" }}>
              {value || "—"}
            </div>
          </div>
        ))}
        <div style={{
          background:"#f9fafb",
          border:`1px solid ${tlColor ? tlColor + "50" : "#e5e7eb"}`,
          borderRadius:8, padding:"10px 14px",
        }}>
          <div style={{ fontSize:12, color:"#6b7280", marginBottom:4 }}>Trade Licence Expiry</div>
          <div style={{ fontSize:15, fontWeight:600, color: tlColor ?? (tlExpiry ? "#111827" : "#9ca3af") }}>
            {fmtDate(tlExpiry)}
          </div>
          {tlDays != null && tlDays < 90 && (
            <div style={{ fontSize:11, color:tlColor, marginTop:2 }}>
              {tlDays < 0 ? "Expired" : `Expires in ${tlDays} day${tlDays !== 1 ? "s" : ""}`}
            </div>
          )}
        </div>
      </div>
      {account.notes && (
        <div style={{
          background:"#fffbeb", border:"1px solid #fde68a",
          borderRadius:8, padding:"10px 14px",
        }}>
          <div style={{ fontSize:12, color:"#92400e", fontWeight:600, marginBottom:4 }}>Notes</div>
          <div style={{ fontSize:15, color:"#374151", lineHeight:1.6 }}>{account.notes}</div>
        </div>
      )}
    </div>
  );
}

// ── Aging bar + live pending bills ───────────────────────────────────────────

function AgingBar({ aging }) {
  const total = (aging.days0to30 ?? 0) + (aging.days31to60 ?? 0) + (aging.days61to90 ?? 0)
              + (aging.days91to120 ?? 0) + (aging.over120 ?? 0);
  if (total === 0) return null;
  const buckets = [
    { label:"0–30d",   key:"days0to30",   color:"#15803d" },
    { label:"31–60d",  key:"days31to60",  color:"#ca8a04" },
    { label:"61–90d",  key:"days61to90",  color:"#ea580c" },
    { label:"91–120d", key:"days91to120", color:"#dc2626" },
    { label:"120d+",   key:"over120",     color:"#7f1d1d" },
  ];
  return (
    <div style={{ marginBottom:16 }}>
      <div style={{ display:"flex", height:10, borderRadius:4, overflow:"hidden", marginBottom:8 }}>
        {buckets.map(b => {
          const pct = ((aging[b.key] ?? 0) / total) * 100;
          return pct > 0
            ? <div key={b.key} style={{ width:`${pct}%`, background:b.color }}
                title={`${b.label}: AED ${fmt(aging[b.key])}`} />
            : null;
        })}
      </div>
      <div style={{ display:"flex", gap:16, flexWrap:"wrap" }}>
        {buckets.filter(b => (aging[b.key] ?? 0) > 0).map(b => (
          <div key={b.key} style={{ fontSize:11 }}>
            <span style={{ display:"inline-block", width:8, height:8, borderRadius:2,
              background:b.color, marginRight:4 }} />
            <span style={{ color:"#6b7280" }}>{b.label}:</span>
            <span style={{ fontWeight:600, marginLeft:4 }}>AED {fmt(aging[b.key])}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function PendingBills({ accountId }) {
  const [data,    setData]    = useState(null);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState(null);

  const load = useCallback(() => {
    setLoading(true); setError(null);
    api.fetchAccountOutstanding(accountId)
      .then(setData)
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  }, [accountId]);

  useEffect(() => { load(); }, [load]);

  if (loading) return <div style={{ color:"#9ca3af", fontSize:12, padding:"12px 0" }}>Fetching bills from Tally…</div>;
  if (error)   return (
    <div className="alert-warn" style={{ fontSize:12, padding:"8px 12px" }}>
      Bills unavailable: {error}
      <button className="btn" style={{ fontSize:11, padding:"2px 8px", marginLeft:10 }} onClick={load}>Retry</button>
    </div>
  );
  if (!data || data.bills?.length === 0) return (
    <div style={{ color:"#9ca3af", fontSize:13, padding:"12px 0" }}>No pending bills.</div>
  );

  const grouped = {};
  for (const b of data.bills) {
    if (!grouped[b.businessUnit]) grouped[b.businessUnit] = [];
    grouped[b.businessUnit].push(b);
  }

  return (
    <div>
      <AgingBar aging={data.aging} />
      {Object.entries(grouped).map(([unit, bills]) => (
        <div key={unit} style={{ marginBottom:20 }}>
          <div style={{ display:"flex", alignItems:"center", gap:8, marginBottom:6 }}>
            <span style={{
              background: COMPANY_ACCENT[unit] ?? "#374151", color:"#fff",
              borderRadius:4, padding:"2px 8px", fontSize:11, fontWeight:700,
            }}>{unit}</span>
            <span style={{ fontSize:12, color:"#6b7280" }}>
              {bills.length} pending bill{bills.length !== 1 ? "s" : ""}
            </span>
          </div>
          <table className="data-table" style={{ marginBottom:0 }}>
            <thead>
              <tr>
                <th style={{ fontFamily:"monospace" }}>Invoice Ref</th>
                <th>Bill Date</th>
                <th>Due Date</th>
                <th style={{ textAlign:"right" }}>Pending (AED)</th>
                <th style={{ textAlign:"center", width:110 }}>Age</th>
              </tr>
            </thead>
            <tbody>
              {bills.map((b, i) => {
                const age = b.daysFromBillDate ?? 0;
                const { label: ageLabel, color: ageColor } =
                  age <= 30  ? { label:`${age}d`,  color:"#15803d" } :
                  age <= 60  ? { label:`${age}d`,  color:"#ca8a04" } :
                  age <= 90  ? { label:`${age}d`,  color:"#ea580c" } :
                  age <= 120 ? { label:`${age}d`,  color:"#dc2626" } :
                               { label:`${age}d`,  color:"#7f1d1d" };
                return (
                  <tr key={i} style={{ background: age > 120 ? "#fff5f5" : undefined }}>
                    <td style={{ fontFamily:"monospace", fontWeight:600, fontSize:12 }}>{b.invoiceRef}</td>
                    <td style={{ fontSize:12 }}>{fmtDate(b.billDate)}</td>
                    <td style={{ fontSize:12, color: b.overdue ? "#b91c1c" : "#374151" }}>{fmtDate(b.dueDate)}</td>
                    <td style={{ textAlign:"right", fontWeight:600, fontFamily:"monospace", fontSize:12 }}>
                      {fmt(b.pendingAmount)}
                    </td>
                    <td style={{ textAlign:"center", fontSize:12, fontWeight:600, color:ageColor }}>
                      {ageLabel}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      ))}
      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center",
        fontSize:11, color:"#9ca3af", marginTop:4 }}>
        <span>Total pending: <strong style={{ color:"#111827" }}>AED {fmt(data.totalOutstanding)}</strong></span>
        <span>
          Fetched {new Date(data.fetchedAt).toLocaleTimeString("en-AE", { hour:"2-digit", minute:"2-digit" })}
          <button className="btn" style={{ fontSize:11, padding:"2px 8px", marginLeft:8 }} onClick={load}>Refresh</button>
        </span>
      </div>
    </div>
  );
}

// ── Overview tab (account details + financial summary + pending bills) ────────

function LedgerTable({ unit, ledgers }) {
  const subtotalOut = ledgers.reduce((s, l) => s + (l.outstanding ?? 0), 0);
  const subtotalCL  = ledgers.reduce((s, l) => s + (l.tallyCredit  ?? 0), 0);
  const accent      = COMPANY_ACCENT[unit] ?? "#374151";
  return (
    <div style={{ marginBottom:20 }}>
      <div style={{ display:"flex", alignItems:"center", gap:8, marginBottom:8 }}>
        <span style={{
          background:accent, color:"#fff",
          borderRadius:4, padding:"2px 10px", fontSize:11, fontWeight:700,
        }}>{unit}</span>
        <span style={{ fontSize:12, color:"#6b7280" }}>
          {ledgers.length} ledger{ledgers.length !== 1 ? "s" : ""}
        </span>
      </div>
      <table className="data-table" style={{ marginBottom:0 }}>
        <thead>
          <tr>
            <th>Account Name (Ledger)</th>
            <th style={{ textAlign:"right" }}>Outstanding (AED)</th>
            <th style={{ textAlign:"right" }}>Credit Limit (AED)</th>
            <th style={{ textAlign:"center", width:110 }}>Credit Period</th>
          </tr>
        </thead>
        <tbody>
          {ledgers.map((l, i) => (
            <tr key={i}>
              <td style={{ fontSize:13 }}>{l.ledgerName}</td>
              <td style={{ textAlign:"right", fontWeight:600,
                color: l.outstanding == null ? "#b91c1c" : l.isDr ? "#111827" : "#15803d" }}>
                {l.outstanding == null ? "Tally unreachable" : fmt(l.outstanding)}
              </td>
              <td style={{ textAlign:"right", fontSize:12, color: l.tallyCredit ? "#374151" : "#9ca3af" }}>
                {l.tallyCredit ? fmt(l.tallyCredit) : "—"}
              </td>
              <td style={{ textAlign:"center", fontSize:12, color:"#374151" }}>
                {l.creditPeriod || <span style={{ color:"#9ca3af" }}>—</span>}
              </td>
            </tr>
          ))}
          <tr style={{ background:"#f3f4f6" }}>
            <td style={{ fontWeight:700, fontSize:12 }}>Subtotal</td>
            <td style={{ textAlign:"right", fontWeight:700, fontSize:13,
              color: subtotalOut > 0 ? "#111827" : "#15803d" }}>
              AED {fmt(subtotalOut)}
            </td>
            <td style={{ textAlign:"right", fontWeight:700, fontSize:13, color:"#374151" }}>
              {subtotalCL > 0 ? `AED ${fmt(subtotalCL)}` : "—"}
            </td>
            <td />
          </tr>
        </tbody>
      </table>
    </div>
  );
}

function OverviewTab({ account, accountId, initialData }) {
  const [data,    setData]    = useState(initialData ?? null);
  const [loading, setLoading] = useState(initialData == null);
  const [error,   setError]   = useState(null);

  const load = useCallback(() => {
    setLoading(true); setError(null);
    api.fetchAccountFinancial(accountId)
      .then(setData)
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  }, [accountId]);

  useEffect(() => { if (initialData == null) load(); }, [load, initialData]);

  const cachedTime = data?.cachedAt
    ? new Date(data.cachedAt).toLocaleTimeString("en-AE", { hour:"2-digit", minute:"2-digit" })
    : null;

  const grouped = {};
  for (const l of data?.ledgers ?? []) {
    if (!grouped[l.businessUnit]) grouped[l.businessUnit] = [];
    grouped[l.businessUnit].push(l);
  }
  const units = Object.keys(grouped);

  return (
    <div style={{ padding:"20px 0" }}>

      {/* ── Account details ── */}
      <AccountDetailsPanel account={account} />

      <hr style={{ border:"none", borderTop:"1px solid #e5e7eb", margin:"0 0 24px" }} />

      {/* ── Financial Overview / Tally ledgers ── */}
      <div style={{ fontWeight:700, fontSize:13, marginBottom:12 }}>Financial Overview</div>

      {loading && <div style={{ color:"#9ca3af", fontSize:12, marginBottom:20 }}>Loading from Tally…</div>}
      {error   && <div className="alert-warn" style={{ padding:"8px 12px", marginBottom:16, fontSize:12 }}>{error}</div>}

      {!loading && data && (
        <>
          {data.error && (
            <div className="alert-warn" style={{ padding:"8px 12px", marginBottom:16, fontSize:12 }}>
              Tally error: {data.error}
            </div>
          )}
          {units.length === 0 && (
            <div style={{ color:"#9ca3af", fontSize:13, marginBottom:20 }}>
              No Tally ledgers linked to this account.
            </div>
          )}
          {units.map(unit => (
            <LedgerTable key={unit} unit={unit} ledgers={grouped[unit]} />
          ))}
          {units.length > 1 && (
            <div style={{
              background:"#1e3a5f", border:"1px solid #1e40af",
              borderRadius:8, padding:"14px 16px", marginBottom:20,
              display:"flex", justifyContent:"space-between", alignItems:"center",
            }}>
              <div>
                <div style={{ fontSize:11, color:"#93c5fd", marginBottom:4 }}>Total Outstanding (All Companies)</div>
                <div style={{ fontSize:22, fontWeight:700, color:"#fff" }}>AED {fmt(data.totalOutstanding)}</div>
              </div>
              {cachedTime && (
                <span style={{ fontSize:11, color:"#93c5fd" }}>
                  Updated {cachedTime}
                  <button className="btn" style={{ fontSize:11, padding:"2px 8px", marginLeft:8 }}
                    onClick={load}>Refresh</button>
                </span>
              )}
            </div>
          )}
          {units.length === 1 && cachedTime && (
            <div style={{ textAlign:"right", fontSize:11, color:"#9ca3af", marginBottom:16 }}>
              Updated {cachedTime}
              <button className="btn" style={{ fontSize:11, padding:"2px 8px", marginLeft:8 }}
                onClick={load}>Refresh</button>
            </div>
          )}
        </>
      )}

      <hr style={{ border:"none", borderTop:"1px solid #e5e7eb", margin:"0 0 20px" }} />

      {/* ── Pending Bills ── */}
      <div style={{ fontWeight:700, fontSize:13, marginBottom:12 }}>Pending Bills</div>
      <PendingBills accountId={accountId} />

    </div>
  );
}

// ── Key Dates sub-panel (inside a contact row) ────────────────────────────────

function KeyDatesPanel({ contactId, canEdit, canManage }) {
  const showToast = useToast();
  const [dates,    setDates]    = useState(null);
  const [loading,  setLoading]  = useState(true);
  const [err,      setErr]      = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [editing,  setEditing]  = useState(null); // keyDate object being edited
  const [saving,   setSaving]   = useState(false);

  const emptyForm = { dateType:"BIRTHDAY", label:"", month:"", day:"", notes:"" };
  const [form, setForm] = useState(emptyForm);

  const load = useCallback(() => {
    setLoading(true);
    api.fetchKeyDates(contactId)
      .then(d => { setDates(d); setErr(null); })
      .catch(e => setErr(e.message))
      .finally(() => setLoading(false));
  }, [contactId]);

  useEffect(() => { load(); }, [load]);

  function startAdd()          { setForm(emptyForm); setEditing(null); setShowForm(true); }
  function startEdit(kd)       { setForm({ dateType:kd.dateType, label:kd.label, month:String(kd.month), day:String(kd.day), notes:kd.notes ?? "" }); setEditing(kd); setShowForm(true); }
  function cancelForm()        { setShowForm(false); setEditing(null); }

  async function handleSave(e) {
    e.preventDefault();
    setSaving(true); setErr(null);
    const body = { dateType:form.dateType, label:form.label, month:Number(form.month), day:Number(form.day), notes:form.notes || null };
    try {
      editing ? await api.updateKeyDate(editing.id, body) : await api.createKeyDate(contactId, body);
      showToast(editing ? "Key date updated" : "Key date added");
      cancelForm(); load();
    } catch(ex) { setErr(ex.message); }
    finally { setSaving(false); }
  }

  async function handleDelete(id) {
    if (!window.confirm("Delete this key date?")) return;
    setSaving(true);
    try { await api.deleteKeyDate(id); showToast("Key date deleted", "info"); load(); }
    catch(ex) { setErr(ex.message); }
    finally { setSaving(false); }
  }

  return (
    <div style={{ marginTop:10, paddingTop:10, borderTop:"1px dashed #e5e7eb" }}>
      <div style={{ display:"flex", alignItems:"center", gap:10, marginBottom:8 }}>
        <span style={{ fontSize:12, color:"#6b7280", fontWeight:600 }}>Key Dates</span>
        {canEdit && !showForm && (
          <button className="btn" style={{ fontSize:11, padding:"2px 8px" }} onClick={startAdd}>+ Add</button>
        )}
      </div>

      {err && <InlineErr msg={err} />}

      {showForm && (
        <form onSubmit={handleSave} style={{
          background:"#fff", border:"1px solid #e5e7eb",
          borderRadius:6, padding:12, marginBottom:10,
        }}>
          <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:8, marginBottom:8 }}>
            <div>
              <label style={{ fontSize:11, color:"#6b7280", display:"block", marginBottom:2 }}>Type</label>
              <select className="form-input" value={form.dateType}
                onChange={e => setForm(f => ({...f, dateType:e.target.value}))}>
                {DATE_TYPES.map(t => <option key={t}>{t}</option>)}
              </select>
            </div>
            <div>
              <label style={{ fontSize:11, color:"#6b7280", display:"block", marginBottom:2 }}>Label *</label>
              <input className="form-input" required placeholder="e.g. Wife's Birthday"
                value={form.label} onChange={e => setForm(f => ({...f, label:e.target.value}))} />
            </div>
            <div>
              <label style={{ fontSize:11, color:"#6b7280", display:"block", marginBottom:2 }}>Month</label>
              <select className="form-input" required value={form.month}
                onChange={e => setForm(f => ({...f, month:e.target.value}))}>
                <option value="">Select…</option>
                {MONTH_NAMES.map((m,i) => <option key={i+1} value={i+1}>{m}</option>)}
              </select>
            </div>
            <div>
              <label style={{ fontSize:11, color:"#6b7280", display:"block", marginBottom:2 }}>Day</label>
              <input className="form-input" type="number" required min="1" max="31"
                placeholder="1–31" value={form.day}
                onChange={e => setForm(f => ({...f, day:e.target.value}))} />
            </div>
          </div>
          <div style={{ marginBottom:8 }}>
            <label style={{ fontSize:11, color:"#6b7280", display:"block", marginBottom:2 }}>Notes</label>
            <input className="form-input" value={form.notes}
              onChange={e => setForm(f => ({...f, notes:e.target.value}))} />
          </div>
          <div style={{ display:"flex", gap:6 }}>
            <button className="btn btn-primary" type="submit" disabled={saving} style={{ fontSize:11 }}>
              {saving ? "Saving…" : editing ? "Update" : "Add"}
            </button>
            <button className="btn" type="button" style={{ fontSize:11 }} onClick={cancelForm}>Cancel</button>
          </div>
        </form>
      )}

      {loading && <div style={{ fontSize:12, color:"#9ca3af" }}>Loading…</div>}
      {!loading && dates?.length === 0 && !showForm && (
        <div style={{ fontSize:12, color:"#9ca3af" }}>No key dates added.</div>
      )}
      {dates?.map(kd => (
        <div key={kd.id} style={{
          display:"flex", alignItems:"center", justifyContent:"space-between",
          padding:"4px 0", borderBottom:"1px solid #f3f4f6", fontSize:12,
        }}>
          <div>
            <span style={{ fontWeight:600 }}>{kd.label}</span>
            <span style={{ color:"#9ca3af", marginLeft:8 }}>
              {MONTH_NAMES[(kd.month ?? 1) - 1]} {kd.day}
            </span>
            <span style={{
              marginLeft:8, fontSize:10, background:"#f3f4f6", color:"#6b7280",
              borderRadius:3, padding:"1px 5px",
            }}>{kd.dateType}</span>
            {kd.notes && <span style={{ marginLeft:8, color:"#9ca3af" }}>{kd.notes}</span>}
          </div>
          {canEdit && (
            <div style={{ display:"flex", gap:4 }}>
              <button className="btn" style={{ fontSize:10, padding:"1px 6px" }}
                onClick={() => startEdit(kd)}>Edit</button>
              {canManage && (
                <button className="btn" style={{ fontSize:10, padding:"1px 6px", color:"#b91c1c" }}
                  onClick={() => handleDelete(kd.id)}>Del</button>
              )}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

// ── Contacts tab ──────────────────────────────────────────────────────────────

function ContactsTab({ accountId, myPerms }) {
  const showToast = useToast();
  const [contacts,   setContacts]   = useState([]);
  const [loading,    setLoading]    = useState(true);
  const [err,        setErr]        = useState(null);
  const [showForm,   setShowForm]   = useState(false);
  const [editing,    setEditing]    = useState(null); // contact object
  const [saving,     setSaving]     = useState(false);
  const [expanded,   setExpanded]   = useState({}); // contactId → bool

  const canEdit   = myPerms.includes("CRM_EDIT");
  const canManage = myPerms.includes("CRM_MANAGE");

  const emptyForm = { name:"", role:"", email:"", phone:"", primary:false, wife:"", kids:"", remarks:"", notes:"" };
  const [form, setForm] = useState(emptyForm);

  const load = useCallback(() => {
    setLoading(true);
    api.fetchAccountContacts(accountId)
      .then(c => { setContacts(c); setErr(null); })
      .catch(e => setErr(e.message))
      .finally(() => setLoading(false));
  }, [accountId]);

  useEffect(() => { load(); }, [load]);

  function startAdd()        { setForm(emptyForm); setEditing(null); setShowForm(true); }
  function startEdit(c)      { setForm({ name:c.name, role:c.role??"", email:c.email??"", phone:c.phone??"", primary:c.isPrimary??false, wife:c.wife??"", kids:c.kids??"", remarks:c.remarks??"", notes:c.notes??"" }); setEditing(c); setShowForm(true); }
  function cancelForm()      { setShowForm(false); setEditing(null); }
  function toggleExpand(id)  { setExpanded(ex => ({ ...ex, [id]: !ex[id] })); }

  async function handleSave(e) {
    e.preventDefault();
    setSaving(true); setErr(null);
    const body = {
      name: form.name, role: form.role || null, email: form.email || null,
      phone: form.phone || null, primary: form.primary,
      wife: form.wife || null, kids: form.kids || null,
      remarks: form.remarks || null, notes: form.notes || null,
    };
    try {
      editing ? await api.updateContact(editing.id, body) : await api.createContact(accountId, body);
      showToast(editing ? "Contact updated" : "Contact added");
      cancelForm(); load();
    } catch(ex) { setErr(ex.message); }
    finally { setSaving(false); }
  }

  async function handleDelete(id) {
    if (!window.confirm("Delete this contact?")) return;
    setSaving(true);
    try { await api.deleteContact(id); showToast("Contact deleted", "info"); load(); }
    catch(ex) { setErr(ex.message); }
    finally { setSaving(false); }
  }

  return (
    <div style={{ padding:"20px 0" }}>
      <InlineErr msg={err} />

      <div style={{ display:"flex", alignItems:"center", gap:12, marginBottom:16 }}>
        <span style={{ fontWeight:700, fontSize:13 }}>
          Contacts <span style={{ color:"#9ca3af", fontWeight:400 }}>({contacts.length})</span>
        </span>
        {canEdit && !showForm && (
          <button className="btn btn-primary" style={{ fontSize:12 }} onClick={startAdd}>+ Add Contact</button>
        )}
      </div>

      {/* Add / Edit form */}
      {showForm && (
        <form onSubmit={handleSave} style={{
          background:"#f9fafb", border:"1px solid #e5e7eb",
          borderRadius:8, padding:16, marginBottom:20,
        }}>
          <div style={{ fontWeight:600, fontSize:13, marginBottom:14 }}>
            {editing ? "Edit Contact" : "New Contact"}
          </div>
          <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:12, marginBottom:12 }}>
            <div>
              <label style={{ fontSize:11, color:"#6b7280", display:"block", marginBottom:3 }}>Name *</label>
              <input className="form-input" required value={form.name}
                onChange={e => setForm(f => ({...f, name:e.target.value}))} />
            </div>
            <div>
              <label style={{ fontSize:11, color:"#6b7280", display:"block", marginBottom:3 }}>Role / Title</label>
              <input className="form-input" value={form.role}
                onChange={e => setForm(f => ({...f, role:e.target.value}))} />
            </div>
            <div>
              <label style={{ fontSize:11, color:"#6b7280", display:"block", marginBottom:3 }}>Phone</label>
              <input className="form-input" value={form.phone}
                onChange={e => setForm(f => ({...f, phone:e.target.value}))} />
            </div>
            <div>
              <label style={{ fontSize:11, color:"#6b7280", display:"block", marginBottom:3 }}>Email</label>
              <input className="form-input" type="email" value={form.email}
                onChange={e => setForm(f => ({...f, email:e.target.value}))} />
            </div>
            <div>
              <label style={{ fontSize:11, color:"#6b7280", display:"block", marginBottom:3 }}>Wife's Name</label>
              <input className="form-input" value={form.wife}
                onChange={e => setForm(f => ({...f, wife:e.target.value}))} />
            </div>
            <div>
              <label style={{ fontSize:11, color:"#6b7280", display:"block", marginBottom:3 }}>Kids</label>
              <input className="form-input" placeholder="e.g. Ahmed (12), Sara (9)"
                value={form.kids}
                onChange={e => setForm(f => ({...f, kids:e.target.value}))} />
            </div>
          </div>
          <div style={{ marginBottom:12 }}>
            <label style={{ fontSize:11, color:"#6b7280", display:"block", marginBottom:3 }}>Remarks</label>
            <input className="form-input" placeholder="e.g. Prefers WhatsApp, best time evenings"
              value={form.remarks}
              onChange={e => setForm(f => ({...f, remarks:e.target.value}))} />
          </div>
          <div style={{ marginBottom:12 }}>
            <label style={{ fontSize:11, color:"#6b7280", display:"block", marginBottom:3 }}>Notes</label>
            <textarea className="form-input" rows={2} style={{ resize:"vertical" }} value={form.notes}
              onChange={e => setForm(f => ({...f, notes:e.target.value}))} />
          </div>
          <div style={{ display:"flex", alignItems:"center", gap:12, marginBottom:14 }}>
            <label style={{ display:"flex", alignItems:"center", gap:6, fontSize:12, cursor:"pointer" }}>
              <input type="checkbox" checked={form.primary}
                onChange={e => setForm(f => ({...f, primary:e.target.checked}))} />
              Primary contact
            </label>
          </div>
          <div style={{ display:"flex", gap:8 }}>
            <button className="btn btn-primary" type="submit" disabled={saving} style={{ fontSize:12 }}>
              {saving ? "Saving…" : editing ? "Update" : "Add Contact"}
            </button>
            <button className="btn" type="button" style={{ fontSize:12 }} onClick={cancelForm}>Cancel</button>
          </div>
        </form>
      )}

      {loading && <div style={{ color:"#9ca3af", fontSize:13 }}>Loading…</div>}
      {!loading && contacts.length === 0 && !showForm && (
        <EmptyNote text="No contacts added yet." />
      )}

      {contacts.map(c => (
        <div key={c.id} style={{
          border:"1px solid #e5e7eb", borderRadius:8,
          marginBottom:10, overflow:"hidden",
        }}>
          {/* Contact row */}
          <div style={{
            display:"flex", alignItems:"center", gap:10,
            padding:"12px 14px", background:"#fff",
          }}>
            <div style={{ flex:1, minWidth:0 }}>
              <div style={{ display:"flex", alignItems:"center", gap:8, flexWrap:"wrap" }}>
                <span style={{ fontWeight:600, fontSize:13 }}>{c.name}</span>
                {c.isPrimary && (
                  <span style={{ fontSize:10, background:"#eff6ff", color:"#1d4ed8",
                    border:"1px solid #bfdbfe", borderRadius:4, padding:"1px 6px" }}>
                    Primary
                  </span>
                )}
                {c.role && <span style={{ fontSize:12, color:"#6b7280" }}>{c.role}</span>}
              </div>
              <div style={{ display:"flex", gap:14, marginTop:3, flexWrap:"wrap" }}>
                {c.phone && <span style={{ fontSize:12, color:"#374151" }}>{c.phone}</span>}
                {c.email && <span style={{ fontSize:12, color:"#374151" }}>{c.email}</span>}
                {c.wife  && <span style={{ fontSize:12, color:"#6b7280" }}>Wife: {c.wife}</span>}
                {c.kids  && <span style={{ fontSize:12, color:"#6b7280" }}>Kids: {c.kids}</span>}
              </div>
              {c.remarks && (
                <div style={{ fontSize:11, color:"#9ca3af", marginTop:3 }}>{c.remarks}</div>
              )}
            </div>

            <div style={{ display:"flex", gap:6, alignItems:"center", flexShrink:0 }}>
              <button className="btn" style={{ fontSize:11, padding:"3px 8px" }}
                onClick={() => toggleExpand(c.id)}>
                {expanded[c.id] ? "Hide dates" : "Key dates"}
              </button>
              {canEdit && (
                <>
                  <button className="btn" style={{ fontSize:11, padding:"3px 8px" }}
                    onClick={() => startEdit(c)}>Edit</button>
                  {canManage && (
                    <button className="btn" style={{ fontSize:11, padding:"3px 8px", color:"#b91c1c" }}
                      onClick={() => handleDelete(c.id)}>Delete</button>
                  )}
                </>
              )}
            </div>
          </div>

          {/* Key dates expandable */}
          {expanded[c.id] && (
            <div style={{ padding:"0 14px 14px", background:"#fafafa" }}>
              <KeyDatesPanel contactId={c.id} canEdit={canEdit} canManage={canManage} />
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

// ── Customer Details tab (Applications, Products, Machines) ───────────────────

function ApplicationsSection({ accountId, canEdit, canManage }) {
  const showToast = useToast();
  const [items,    setItems]    = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [err,      setErr]      = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [editing,  setEditing]  = useState(null);
  const [saving,   setSaving]   = useState(false);
  const emptyForm = { application:"", notes:"" };
  const [form, setForm]         = useState(emptyForm);

  const load = useCallback(() => {
    setLoading(true);
    api.fetchApplications(accountId)
      .then(d => { setItems(d); setErr(null); })
      .catch(e => setErr(e.message))
      .finally(() => setLoading(false));
  }, [accountId]);

  useEffect(() => { load(); }, [load]);

  function startAdd()       { setForm(emptyForm); setEditing(null); setShowForm(true); }
  function startEdit(item)  { setForm({ application:item.application, notes:item.notes??""  }); setEditing(item); setShowForm(true); }
  function cancel()         { setShowForm(false); setEditing(null); }

  async function handleSave(e) {
    e.preventDefault();
    setSaving(true); setErr(null);
    const body = { application:form.application, notes:form.notes || null };
    try {
      editing ? await api.updateApplication(editing.id, body) : await api.createApplication(accountId, body);
      showToast(editing ? "Application updated" : "Application added");
      cancel(); load();
    } catch(ex) { setErr(ex.message); }
    finally { setSaving(false); }
  }

  async function handleDelete(id) {
    if (!window.confirm("Remove this application?")) return;
    setSaving(true);
    try { await api.deleteApplication(id); showToast("Application removed", "info"); load(); }
    catch(ex) { setErr(ex.message); }
    finally { setSaving(false); }
  }

  return (
    <div style={{ marginBottom:32 }}>
      <SectionHeader
        title="Adhesive Applications"
        action={canEdit && !showForm && (
          <button className="btn" style={{ fontSize:11 }} onClick={startAdd}>+ Add</button>
        )}
      />
      <InlineErr msg={err} />

      {showForm && (
        <form onSubmit={handleSave} style={{
          background:"#f9fafb", border:"1px solid #e5e7eb",
          borderRadius:8, padding:14, marginBottom:14,
        }}>
          <div style={{ display:"grid", gridTemplateColumns:"2fr 1fr", gap:10, marginBottom:10 }}>
            <div>
              <label style={{ fontSize:11, color:"#6b7280", display:"block", marginBottom:3 }}>Application *</label>
              <input className="form-input" required
                placeholder="e.g. Tissue Lamination, Box Closing"
                value={form.application}
                onChange={e => setForm(f => ({...f, application:e.target.value}))} />
            </div>
            <div>
              <label style={{ fontSize:11, color:"#6b7280", display:"block", marginBottom:3 }}>Notes</label>
              <input className="form-input" value={form.notes}
                onChange={e => setForm(f => ({...f, notes:e.target.value}))} />
            </div>
          </div>
          <div style={{ display:"flex", gap:8 }}>
            <button className="btn btn-primary" type="submit" disabled={saving} style={{ fontSize:12 }}>
              {saving ? "Saving…" : editing ? "Update" : "Add"}
            </button>
            <button className="btn" type="button" style={{ fontSize:12 }} onClick={cancel}>Cancel</button>
          </div>
        </form>
      )}

      {loading && <div style={{ fontSize:13, color:"#9ca3af" }}>Loading…</div>}
      {!loading && items.length === 0 && !showForm && <EmptyNote text="No applications recorded." />}

      <div style={{ display:"flex", flexWrap:"wrap", gap:8 }}>
        {items.map(item => (
          <div key={item.id} style={{
            background:"#eff6ff", border:"1px solid #bfdbfe", borderRadius:20,
            padding:"4px 12px", fontSize:12, display:"flex", alignItems:"center", gap:6,
          }}>
            <span>{item.application}</span>
            {item.notes && <span style={{ color:"#9ca3af" }}>· {item.notes}</span>}
            {canEdit && (
              <>
                <button style={{ border:"none", background:"none", cursor:"pointer",
                  fontSize:11, color:"#1d4ed8", padding:0 }}
                  onClick={() => startEdit(item)}>edit</button>
                {canManage && (
                  <button style={{ border:"none", background:"none", cursor:"pointer",
                    fontSize:11, color:"#b91c1c", padding:0 }}
                    onClick={() => handleDelete(item.id)}>×</button>
                )}
              </>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

function ProductsSection({ accountId, canEdit, canManage }) {
  const showToast = useToast();
  const [items,   setItems]   = useState([]);
  const [fgList,  setFgList]  = useState([]);
  const [loading, setLoading] = useState(true);
  const [err,     setErr]     = useState(null);
  const [showForm,setShowForm]= useState(false);
  const [saving,  setSaving]  = useState(false);
  const [form,    setForm]    = useState({ fgId:"", notes:"" });

  const load = useCallback(() => {
    setLoading(true);
    Promise.all([
      api.fetchAccountProducts(accountId),
      api.fetchFGs(),
    ])
      .then(([prods, fgs]) => { setItems(prods); setFgList(fgs); setErr(null); })
      .catch(e => setErr(e.message))
      .finally(() => setLoading(false));
  }, [accountId]);

  useEffect(() => { load(); }, [load]);

  async function handleSave(e) {
    e.preventDefault();
    if (!form.fgId) return;
    setSaving(true); setErr(null);
    try {
      await api.createAccountProduct(accountId, { fgId:form.fgId, notes:form.notes || null });
      showToast("Product linked");
      setShowForm(false); setForm({ fgId:"", notes:"" }); load();
    } catch(ex) { setErr(ex.message); }
    finally { setSaving(false); }
  }

  async function handleDelete(id) {
    if (!window.confirm("Remove this product link?")) return;
    setSaving(true);
    try { await api.deleteAccountProduct(id); showToast("Product removed", "info"); load(); }
    catch(ex) { setErr(ex.message); }
    finally { setSaving(false); }
  }

  const linkedFgIds = new Set(items.map(i => i.fgId));
  const available   = fgList.filter(fg => !linkedFgIds.has(fg.id));

  function fgLabel(fgId) {
    const fg = fgList.find(f => f.id === fgId);
    return fg ? `${fg.id} – ${fg.name}` : fgId;
  }

  return (
    <div style={{ marginBottom:32 }}>
      <SectionHeader
        title="Products Used / Trialled"
        action={canEdit && !showForm && available.length > 0 && (
          <button className="btn" style={{ fontSize:11 }} onClick={() => setShowForm(true)}>+ Link Product</button>
        )}
      />
      <InlineErr msg={err} />

      {showForm && (
        <form onSubmit={handleSave} style={{
          background:"#f9fafb", border:"1px solid #e5e7eb",
          borderRadius:8, padding:14, marginBottom:14,
        }}>
          <div style={{ display:"grid", gridTemplateColumns:"2fr 1fr", gap:10, marginBottom:10 }}>
            <div>
              <label style={{ fontSize:11, color:"#6b7280", display:"block", marginBottom:3 }}>Product (FG) *</label>
              <select className="form-input" required value={form.fgId}
                onChange={e => setForm(f => ({...f, fgId:e.target.value}))}>
                <option value="">Select product…</option>
                {available.map(fg => (
                  <option key={fg.id} value={fg.id}>{fg.id} – {fg.name}</option>
                ))}
              </select>
            </div>
            <div>
              <label style={{ fontSize:11, color:"#6b7280", display:"block", marginBottom:3 }}>Notes</label>
              <input className="form-input" value={form.notes}
                onChange={e => setForm(f => ({...f, notes:e.target.value}))} />
            </div>
          </div>
          <div style={{ display:"flex", gap:8 }}>
            <button className="btn btn-primary" type="submit" disabled={saving || !form.fgId} style={{ fontSize:12 }}>
              {saving ? "Saving…" : "Link"}
            </button>
            <button className="btn" type="button" style={{ fontSize:12 }}
              onClick={() => { setShowForm(false); setForm({ fgId:"", notes:"" }); }}>Cancel</button>
          </div>
        </form>
      )}

      {loading && <div style={{ fontSize:13, color:"#9ca3af" }}>Loading…</div>}
      {!loading && items.length === 0 && !showForm && <EmptyNote text="No products linked yet." />}

      <div style={{ display:"flex", flexWrap:"wrap", gap:8 }}>
        {items.map(item => (
          <div key={item.id} style={{
            background:"#f0fdf4", border:"1px solid #bbf7d0", borderRadius:6,
            padding:"6px 12px", fontSize:12, display:"flex", alignItems:"center", gap:8,
          }}>
            <span style={{ fontWeight:600 }}>{fgLabel(item.fgId)}</span>
            {item.notes && <span style={{ color:"#9ca3af" }}>{item.notes}</span>}
            {canManage && (
              <button style={{ border:"none", background:"none", cursor:"pointer",
                fontSize:13, color:"#b91c1c", padding:0, lineHeight:1 }}
                onClick={() => handleDelete(item.id)}>×</button>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

function MachinesSection({ accountId, canEdit, canManage }) {
  const showToast = useToast();
  const [items,    setItems]    = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [err,      setErr]      = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [editing,  setEditing]  = useState(null);
  const [saving,   setSaving]   = useState(false);

  const emptyForm = { machineType:"", brand:"", model:"", temperatureC:"", speedMMin:"", glueKgHr:"", notes:"" };
  const [form, setForm] = useState(emptyForm);

  const load = useCallback(() => {
    setLoading(true);
    api.fetchMachines(accountId)
      .then(d => { setItems(d); setErr(null); })
      .catch(e => setErr(e.message))
      .finally(() => setLoading(false));
  }, [accountId]);

  useEffect(() => { load(); }, [load]);

  function startAdd()      { setForm(emptyForm); setEditing(null); setShowForm(true); }
  function startEdit(item) { setForm({ machineType:item.machineType, brand:item.brand??"", model:item.model??"", temperatureC:item.temperatureC!=null?String(item.temperatureC):"", speedMMin:item.speedMMin!=null?String(item.speedMMin):"", glueKgHr:item.glueKgHr!=null?String(item.glueKgHr):"", notes:item.notes??""  }); setEditing(item); setShowForm(true); }
  function cancel()        { setShowForm(false); setEditing(null); }

  async function handleSave(e) {
    e.preventDefault();
    setSaving(true); setErr(null);
    const body = {
      machineType: form.machineType,
      brand:       form.brand       || null,
      model:       form.model       || null,
      temperatureC:form.temperatureC !== "" ? Number(form.temperatureC) : null,
      speedMMin:   form.speedMMin   !== "" ? Number(form.speedMMin)   : null,
      glueKgHr:    form.glueKgHr   !== "" ? Number(form.glueKgHr)    : null,
      notes:       form.notes       || null,
    };
    try {
      editing ? await api.updateMachine(editing.id, body) : await api.createMachine(accountId, body);
      showToast(editing ? "Machine updated" : "Machine added");
      cancel(); load();
    } catch(ex) { setErr(ex.message); }
    finally { setSaving(false); }
  }

  async function handleDelete(id) {
    if (!window.confirm("Delete this machine record?")) return;
    setSaving(true);
    try { await api.deleteMachine(id); showToast("Machine deleted", "info"); load(); }
    catch(ex) { setErr(ex.message); }
    finally { setSaving(false); }
  }

  function numOrDash(v) { return v != null ? v : "—"; }

  return (
    <div style={{ marginBottom:32 }}>
      <SectionHeader
        title="Machines"
        action={canEdit && !showForm && (
          <button className="btn" style={{ fontSize:11 }} onClick={startAdd}>+ Add Machine</button>
        )}
      />
      <InlineErr msg={err} />

      {showForm && (
        <form onSubmit={handleSave} style={{
          background:"#f9fafb", border:"1px solid #e5e7eb",
          borderRadius:8, padding:14, marginBottom:16,
        }}>
          <div style={{ fontWeight:600, fontSize:13, marginBottom:12 }}>
            {editing ? "Edit Machine" : "New Machine"}
          </div>
          <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr 1fr", gap:10, marginBottom:10 }}>
            <div style={{ gridColumn:"1 / -1" }}>
              <label style={{ fontSize:11, color:"#6b7280", display:"block", marginBottom:3 }}>Machine Type *</label>
              <input className="form-input" required
                placeholder="e.g. Hot Melt Applicator, Tissue Lamination Line"
                value={form.machineType}
                onChange={e => setForm(f => ({...f, machineType:e.target.value}))} />
            </div>
            <div>
              <label style={{ fontSize:11, color:"#6b7280", display:"block", marginBottom:3 }}>Brand</label>
              <input className="form-input" value={form.brand}
                onChange={e => setForm(f => ({...f, brand:e.target.value}))} />
            </div>
            <div>
              <label style={{ fontSize:11, color:"#6b7280", display:"block", marginBottom:3 }}>Model</label>
              <input className="form-input" value={form.model}
                onChange={e => setForm(f => ({...f, model:e.target.value}))} />
            </div>
            <div />
            <div>
              <label style={{ fontSize:11, color:"#6b7280", display:"block", marginBottom:3 }}>Temperature (°C)</label>
              <input className="form-input" type="number" step="0.1" value={form.temperatureC}
                onChange={e => setForm(f => ({...f, temperatureC:e.target.value}))} />
            </div>
            <div>
              <label style={{ fontSize:11, color:"#6b7280", display:"block", marginBottom:3 }}>Speed (m/min)</label>
              <input className="form-input" type="number" step="0.01" value={form.speedMMin}
                onChange={e => setForm(f => ({...f, speedMMin:e.target.value}))} />
            </div>
            <div>
              <label style={{ fontSize:11, color:"#6b7280", display:"block", marginBottom:3 }}>Glue (kg/hr)</label>
              <input className="form-input" type="number" step="0.001" value={form.glueKgHr}
                onChange={e => setForm(f => ({...f, glueKgHr:e.target.value}))} />
            </div>
          </div>
          <div style={{ marginBottom:10 }}>
            <label style={{ fontSize:11, color:"#6b7280", display:"block", marginBottom:3 }}>Notes</label>
            <input className="form-input" value={form.notes}
              onChange={e => setForm(f => ({...f, notes:e.target.value}))} />
          </div>
          <div style={{ display:"flex", gap:8 }}>
            <button className="btn btn-primary" type="submit" disabled={saving} style={{ fontSize:12 }}>
              {saving ? "Saving…" : editing ? "Update" : "Add"}
            </button>
            <button className="btn" type="button" style={{ fontSize:12 }} onClick={cancel}>Cancel</button>
          </div>
        </form>
      )}

      {loading && <div style={{ fontSize:13, color:"#9ca3af" }}>Loading…</div>}
      {!loading && items.length === 0 && !showForm && <EmptyNote text="No machines recorded." />}

      {items.map(item => (
        <div key={item.id} style={{
          border:"1px solid #e5e7eb", borderRadius:8,
          padding:"12px 14px", marginBottom:10, background:"#fff",
        }}>
          <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start" }}>
            <div>
              <div style={{ fontWeight:600, fontSize:13, marginBottom:4 }}>
                {item.machineType}
                {(item.brand || item.model) && (
                  <span style={{ fontWeight:400, color:"#6b7280", marginLeft:8, fontSize:12 }}>
                    {[item.brand, item.model].filter(Boolean).join(" ")}
                  </span>
                )}
              </div>
              <div style={{ display:"flex", gap:16, fontSize:12, color:"#374151" }}>
                {item.temperatureC != null && (
                  <span><span style={{ color:"#9ca3af" }}>Temp:</span> {numOrDash(item.temperatureC)} °C</span>
                )}
                {item.speedMMin != null && (
                  <span><span style={{ color:"#9ca3af" }}>Speed:</span> {numOrDash(item.speedMMin)} m/min</span>
                )}
                {item.glueKgHr != null && (
                  <span><span style={{ color:"#9ca3af" }}>Glue:</span> {numOrDash(item.glueKgHr)} kg/hr</span>
                )}
              </div>
              {item.notes && (
                <div style={{ fontSize:12, color:"#9ca3af", marginTop:4 }}>{item.notes}</div>
              )}
            </div>
            {canEdit && (
              <div style={{ display:"flex", gap:6, flexShrink:0 }}>
                <button className="btn" style={{ fontSize:11, padding:"3px 8px" }}
                  onClick={() => startEdit(item)}>Edit</button>
                {canManage && (
                  <button className="btn" style={{ fontSize:11, padding:"3px 8px", color:"#b91c1c" }}
                    onClick={() => handleDelete(item.id)}>Delete</button>
                )}
              </div>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}

function CustomerDetailsTab({ accountId, myPerms }) {
  const canEdit   = myPerms.includes("CRM_EDIT");
  const canManage = myPerms.includes("CRM_MANAGE");
  return (
    <div style={{ padding:"20px 0" }}>
      <ApplicationsSection accountId={accountId} canEdit={canEdit} canManage={canManage} />
      <hr style={{ border:"none", borderTop:"1px solid #e5e7eb", marginBottom:28 }} />
      <ProductsSection accountId={accountId} canEdit={canEdit} canManage={canManage} />
      <hr style={{ border:"none", borderTop:"1px solid #e5e7eb", marginBottom:28 }} />
      <MachinesSection accountId={accountId} canEdit={canEdit} canManage={canManage} />
    </div>
  );
}

// ── Credit Applications tab ───────────────────────────────────────────────────

const APP_STATUS = {
  PENDING_APPROVAL: { label:"Pending Approval", bg:"#fefce8", color:"#854d0e", border:"#fde047" },
  APPROVED:         { label:"Approved",          bg:"#f0fdf4", color:"#15803d", border:"#bbf7d0" },
  REJECTED:         { label:"Rejected",          bg:"#fff1f2", color:"#be123c", border:"#fecdd3" },
  ENTERED:          { label:"Entered in System", bg:"#eff6ff", color:"#1d4ed8", border:"#bfdbfe" },
};

function fmtDateTime(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString("en-AE", { day:"2-digit", month:"short", year:"numeric" });
}

function CreditApplications({ accountId, myPerms }) {
  const showToast = useToast();
  const [apps,         setApps]         = useState([]);
  const [loading,      setLoading]      = useState(true);
  const [error,        setError]        = useState(null);
  const [showForm,     setShowForm]     = useState(false);
  const [rejectId,     setRejectId]     = useState(null);
  const [rejectReason, setRejectReason] = useState("");
  const [form,         setForm]         = useState({ creditLimitRequested:"", creditDaysRequested:"", notes:"" });
  const [saving,       setSaving]       = useState(false);

  const canCreate = myPerms.includes("CRM_EDIT");
  const canManage = myPerms.includes("CRM_MANAGE");

  const load = useCallback(() => {
    setLoading(true);
    api.fetchCreditApplications(accountId)
      .then(setApps)
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  }, [accountId]);

  useEffect(() => { load(); }, [load]);

  async function handleCreate(e) {
    e.preventDefault();
    setSaving(true);
    try {
      await api.createCreditApplication(accountId, {
        creditLimitRequested: form.creditLimitRequested ? Number(form.creditLimitRequested) : null,
        creditDaysRequested:  form.creditDaysRequested  ? Number(form.creditDaysRequested)  : null,
        notes: form.notes || null,
      });
      showToast("Credit application submitted");
      setShowForm(false);
      setForm({ creditLimitRequested:"", creditDaysRequested:"", notes:"" });
      load();
    } catch(e) { setError(e.message); }
    finally { setSaving(false); }
  }

  async function handleAction(fn) {
    setSaving(true);
    try { await fn(); showToast("Status updated"); load(); }
    catch(e) { setError(e.message); }
    finally { setSaving(false); }
  }

  return (
    <div style={{ padding:"20px 0" }}>
      {error && <div className="alert-warn" style={{ padding:"8px 12px", marginBottom:12, fontSize:12 }}>{error}</div>}

      <div style={{ display:"flex", alignItems:"center", gap:12, marginBottom:16 }}>
        <span style={{ fontWeight:600, fontSize:13 }}>Credit Applications</span>
        {canCreate && !showForm && (
          <button className="btn btn-primary" style={{ fontSize:12 }}
            onClick={() => setShowForm(true)}>+ New Application</button>
        )}
      </div>

      {showForm && (
        <form onSubmit={handleCreate} style={{
          background:"#f9fafb", border:"1px solid #e5e7eb",
          borderRadius:8, padding:16, marginBottom:20,
        }}>
          <div style={{ fontWeight:600, fontSize:13, marginBottom:12 }}>New Credit Application</div>
          <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:12, marginBottom:12 }}>
            <div>
              <label style={{ fontSize:12, color:"#6b7280", display:"block", marginBottom:4 }}>Credit Limit Requested (AED)</label>
              <input className="form-input" type="number" min="0" step="0.01" placeholder="e.g. 50000"
                value={form.creditLimitRequested}
                onChange={e => setForm(f => ({...f, creditLimitRequested:e.target.value}))} />
            </div>
            <div>
              <label style={{ fontSize:12, color:"#6b7280", display:"block", marginBottom:4 }}>Credit Days Requested</label>
              <input className="form-input" type="number" min="0" placeholder="e.g. 30"
                value={form.creditDaysRequested}
                onChange={e => setForm(f => ({...f, creditDaysRequested:e.target.value}))} />
            </div>
          </div>
          <div style={{ marginBottom:12 }}>
            <label style={{ fontSize:12, color:"#6b7280", display:"block", marginBottom:4 }}>Notes</label>
            <textarea className="form-input" rows={2} style={{ resize:"vertical" }}
              placeholder="Reason for credit application…"
              value={form.notes}
              onChange={e => setForm(f => ({...f, notes:e.target.value}))} />
          </div>
          <div style={{ display:"flex", gap:8 }}>
            <button className="btn btn-primary" type="submit" disabled={saving} style={{ fontSize:12 }}>
              {saving ? "Submitting…" : "Submit"}
            </button>
            <button className="btn" type="button" style={{ fontSize:12 }}
              onClick={() => setShowForm(false)}>Cancel</button>
          </div>
        </form>
      )}

      {rejectId && (
        <div style={{ background:"#fff", border:"1px solid #e5e7eb", borderRadius:8, padding:16, marginBottom:20 }}>
          <div style={{ fontWeight:600, fontSize:13, marginBottom:10 }}>Reject Application</div>
          <textarea className="form-input" rows={2} style={{ marginBottom:10, resize:"vertical" }}
            placeholder="Reason for rejection…"
            value={rejectReason}
            onChange={e => setRejectReason(e.target.value)} />
          <div style={{ display:"flex", gap:8 }}>
            <button className="btn" style={{ fontSize:12, background:"#b91c1c", color:"#fff", border:"none" }}
              disabled={saving || !rejectReason.trim()}
              onClick={() => handleAction(async () => {
                await api.rejectCreditApplication(rejectId, rejectReason);
                setRejectId(null); setRejectReason("");
              })}>Confirm Reject</button>
            <button className="btn" style={{ fontSize:12 }}
              onClick={() => { setRejectId(null); setRejectReason(""); }}>Cancel</button>
          </div>
        </div>
      )}

      {loading && <div style={{ color:"#9ca3af", fontSize:13 }}>Loading…</div>}
      {!loading && apps.length === 0 && <div style={{ color:"#9ca3af", fontSize:13 }}>No credit applications yet.</div>}

      {apps.map(a => {
        const s = APP_STATUS[a.status] ?? APP_STATUS.PENDING_APPROVAL;
        return (
          <div key={a.id} style={{
            background:"#f9fafb", border:"1px solid #e5e7eb",
            borderRadius:8, padding:"14px 16px", marginBottom:12,
          }}>
            <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", marginBottom:8 }}>
              <div>
                <span style={{ background:s.bg, color:s.color, border:`1px solid ${s.border}`,
                  borderRadius:4, padding:"2px 8px", fontSize:11, fontWeight:600 }}>{s.label}</span>
                <span style={{ fontSize:11, color:"#9ca3af", marginLeft:10 }}>
                  {fmtDateTime(a.requestedAt)} · {a.requestedBy}
                </span>
              </div>
              <div style={{ display:"flex", gap:6 }}>
                {canManage && a.status === "PENDING_APPROVAL" && (
                  <>
                    <button className="btn btn-primary" style={{ fontSize:11, padding:"3px 10px" }}
                      disabled={saving}
                      onClick={() => handleAction(() => api.approveCreditApplication(a.id))}>Approve</button>
                    <button className="btn" style={{ fontSize:11, padding:"3px 10px" }}
                      onClick={() => setRejectId(a.id)}>Reject</button>
                  </>
                )}
                {canManage && a.status === "APPROVED" && (
                  <button className="btn btn-primary" style={{ fontSize:11, padding:"3px 10px" }}
                    disabled={saving}
                    onClick={() => handleAction(() => api.enterCreditApplication(a.id))}>
                    Mark Entered in Tally
                  </button>
                )}
              </div>
            </div>
            <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fit, minmax(140px, 1fr))", gap:8, fontSize:12 }}>
              {a.creditLimitRequested != null && (
                <div>
                  <div style={{ color:"#6b7280", fontSize:11 }}>Limit Requested</div>
                  <div style={{ fontWeight:600 }}>AED {fmt(a.creditLimitRequested)}</div>
                </div>
              )}
              {a.creditDaysRequested != null && (
                <div>
                  <div style={{ color:"#6b7280", fontSize:11 }}>Days Requested</div>
                  <div style={{ fontWeight:600 }}>{a.creditDaysRequested} days</div>
                </div>
              )}
              {a.approvedBy && (
                <div>
                  <div style={{ color:"#6b7280", fontSize:11 }}>
                    {a.status === "REJECTED" ? "Rejected by" : "Approved by"}
                  </div>
                  <div>{a.approvedBy} · {fmtDateTime(a.approvedAt)}</div>
                </div>
              )}
              {a.enteredBy && (
                <div>
                  <div style={{ color:"#6b7280", fontSize:11 }}>Entered by</div>
                  <div>{a.enteredBy} · {fmtDateTime(a.enteredAt)}</div>
                </div>
              )}
            </div>
            {a.notes && (
              <div style={{ marginTop:8, fontSize:12, color:"#374151", borderTop:"1px solid #f3f4f6", paddingTop:8 }}>
                {a.notes}
              </div>
            )}
            {a.rejectionReason && (
              <div style={{ marginTop:8, fontSize:12, color:"#b91c1c", borderTop:"1px solid #f3f4f6", paddingTop:8 }}>
                Rejection reason: {a.rejectionReason}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────

const TABS = [
  ["overview",  "Overview"],
  ["contacts",  "Contacts"],
  ["details",   "Customer Details"],
  ["credit",    "Credit Applications"],
];

export function PageCrmAccountDetail({ accountId, onBack, myPerms }) {
  const showToast = useToast();
  const [account,     setAccount]     = useState(null);
  const [financial,   setFinancial]   = useState(null);
  const [loading,     setLoading]     = useState(true);
  const [error,       setError]       = useState(null);
  const [tab,         setTab]         = useState("overview");
  const [showEdit,    setShowEdit]    = useState(false);
  const [editForm,    setEditForm]    = useState(null);
  const [editSaving,  setEditSaving]  = useState(false);
  const [editErr,     setEditErr]     = useState(null);

  const loadData = useCallback(() => {
    setLoading(true); setError(null);
    Promise.all([
      api.fetchCrmAccount(accountId),
      api.fetchAccountFinancial(accountId).catch(() => null),
    ])
      .then(([acct, fin]) => { setAccount(acct); setFinancial(fin); })
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  }, [accountId]);

  useEffect(() => { loadData(); }, [loadData]);

  function startEdit() {
    setEditForm({
      name:               account.name              ?? "",
      vatTrn:             account.vatTrn             ?? "",
      segment:            account.segment            ?? "",
      assignedTo:         account.assignedTo         ?? "",
      creditLimit:        account.creditLimit != null ? String(account.creditLimit) : "",
      creditCurrency:     account.creditCurrency     ?? "AED",
      tradeLicenseExpiry: account.tradeLicenseExpiry ?? "",
      status:             account.status             ?? "PROSPECT",
      notes:              account.notes              ?? "",
    });
    setShowEdit(true); setEditErr(null);
  }

  async function handleEditSave(e) {
    e.preventDefault();
    setEditSaving(true); setEditErr(null);
    try {
      await api.updateCrmAccount(account.id, {
        name:               editForm.name,
        vatTrn:             editForm.vatTrn             || null,
        segment:            editForm.segment            || null,
        assignedTo:         editForm.assignedTo         || null,
        creditLimit:        editForm.creditLimit !== "" ? Number(editForm.creditLimit) : null,
        creditCurrency:     editForm.creditCurrency     || "AED",
        tradeLicenseExpiry: editForm.tradeLicenseExpiry || null,
        status:             editForm.status,
        notes:              editForm.notes              || null,
      });
      showToast("Account updated");
      setShowEdit(false);
      loadData();
    } catch(err) { setEditErr(err.message); }
    finally { setEditSaving(false); }
  }

  if (loading) return <div style={{ padding:32, color:"#9ca3af" }}>Loading…</div>;
  if (error)   return <div className="alert-warn" style={{ margin:16, padding:10 }}>{error}</div>;
  if (!account) return null;

  const badge   = STATUS_BADGE[account.status] ?? STATUS_BADGE.INACTIVE;
  const canEdit = myPerms?.includes("CRM_EDIT");

  return (
    <>
      <div className="page-header">
        <div className="page-header-left">
          <div>
            <div style={{ display:"flex", alignItems:"center", gap:10 }}>
              <div className="page-title">{account.name}</div>
              <span style={{
                background:badge.bg, color:badge.color,
                border:`1px solid ${badge.border}`,
                borderRadius:4, padding:"2px 8px", fontSize:12,
              }}>{account.status}</span>
              <button className="btn" style={{ fontSize:11, padding:"3px 10px" }}
                onClick={loadData}>Refresh</button>
            </div>
            {account.assignedTo && (
              <div className="page-subtitle">Assigned to {account.assignedTo}</div>
            )}
          </div>
        </div>
        {canEdit && !showEdit && (
          <div className="page-header-right">
            <button className="btn" style={{ fontSize:12 }} onClick={startEdit}>Edit Account</button>
          </div>
        )}
      </div>

      {/* Tabs — hidden while editing */}
      {!showEdit && (
        <div style={{ display:"flex", gap:0, borderBottom:"2px solid #e5e7eb", marginBottom:0, overflowX:"auto" }}>
          {TABS.map(([k, l]) => (
            <button key={k}
              onClick={() => setTab(k)}
              style={{
                background:"none", border:"none", cursor:"pointer",
                padding:"10px 18px", fontSize:13, fontWeight:600, whiteSpace:"nowrap",
                color: tab===k ? "#1d4ed8" : "#6b7280",
                borderBottom: tab===k ? "2px solid #1d4ed8" : "2px solid transparent",
                marginBottom:-2,
              }}>
              {l}
            </button>
          ))}
        </div>
      )}

      <div className="card" style={{ marginTop:showEdit ? undefined : 0, borderTopLeftRadius:showEdit ? undefined : 0, borderTopRightRadius:showEdit ? undefined : 0 }}>
        {showEdit && editForm && (
          <div style={{ padding:"20px 0" }}>
            <div style={{ fontWeight:700, fontSize:14, marginBottom:16 }}>Edit Account</div>
            <InlineErr msg={editErr} />
            <form onSubmit={handleEditSave}>
              <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:12, marginBottom:12 }}>
                <div>
                  <label style={{ fontSize:11, color:"#6b7280", display:"block", marginBottom:3 }}>Name *</label>
                  <input className="form-input" required value={editForm.name}
                    onChange={e => setEditForm(f => ({...f, name:e.target.value}))} />
                </div>
                <div>
                  <label style={{ fontSize:11, color:"#6b7280", display:"block", marginBottom:3 }}>Status</label>
                  <select className="form-input" value={editForm.status}
                    onChange={e => setEditForm(f => ({...f, status:e.target.value}))}>
                    <option value="PROSPECT">Prospect</option>
                    <option value="CUSTOMER">Customer</option>
                    <option value="INACTIVE">Inactive</option>
                  </select>
                </div>
                <div>
                  <label style={{ fontSize:11, color:"#6b7280", display:"block", marginBottom:3 }}>Credit Limit</label>
                  <div style={{ display:"flex", gap:6 }}>
                    <input className="form-input" type="number" min="0" step="0.01" style={{ flex:1 }}
                      placeholder="Leave blank = GM Approval"
                      value={editForm.creditLimit}
                      onChange={e => setEditForm(f => ({...f, creditLimit:e.target.value}))} />
                    <select className="form-input" style={{ width:80 }}
                      value={editForm.creditCurrency}
                      onChange={e => setEditForm(f => ({...f, creditCurrency:e.target.value}))}>
                      <option>AED</option><option>USD</option><option>EUR</option>
                    </select>
                  </div>
                </div>
                <div>
                  <label style={{ fontSize:11, color:"#6b7280", display:"block", marginBottom:3 }}>Trade Licence Expiry</label>
                  <input className="form-input" type="date" value={editForm.tradeLicenseExpiry}
                    onChange={e => setEditForm(f => ({...f, tradeLicenseExpiry:e.target.value}))} />
                </div>
                <div>
                  <label style={{ fontSize:11, color:"#6b7280", display:"block", marginBottom:3 }}>VAT TRN</label>
                  <input className="form-input" value={editForm.vatTrn}
                    onChange={e => setEditForm(f => ({...f, vatTrn:e.target.value}))} />
                </div>
                <div>
                  <label style={{ fontSize:11, color:"#6b7280", display:"block", marginBottom:3 }}>Assigned To</label>
                  <input className="form-input" value={editForm.assignedTo}
                    onChange={e => setEditForm(f => ({...f, assignedTo:e.target.value}))} />
                </div>
              </div>
              <div style={{ marginBottom:12 }}>
                <label style={{ fontSize:11, color:"#6b7280", display:"block", marginBottom:3 }}>Segment</label>
                <input className="form-input" style={{ width:"50%" }} value={editForm.segment}
                  onChange={e => setEditForm(f => ({...f, segment:e.target.value}))} />
              </div>
              <div style={{ marginBottom:16 }}>
                <label style={{ fontSize:11, color:"#6b7280", display:"block", marginBottom:3 }}>Notes</label>
                <textarea className="form-input" rows={2} style={{ resize:"vertical" }} value={editForm.notes}
                  onChange={e => setEditForm(f => ({...f, notes:e.target.value}))} />
              </div>
              <div style={{ display:"flex", gap:8 }}>
                <button className="btn btn-primary" type="submit" disabled={editSaving} style={{ fontSize:12 }}>
                  {editSaving ? "Saving…" : "Save"}
                </button>
                <button className="btn" type="button" style={{ fontSize:12 }}
                  onClick={() => setShowEdit(false)}>Cancel</button>
              </div>
            </form>
          </div>
        )}
        {!showEdit && tab === "overview"  && <OverviewTab account={account} accountId={accountId} initialData={financial} />}
        {!showEdit && tab === "contacts"  && <ContactsTab accountId={accountId} myPerms={myPerms} />}
        {!showEdit && tab === "details"   && <CustomerDetailsTab accountId={accountId} myPerms={myPerms} />}
        {!showEdit && tab === "credit"    && <CreditApplications accountId={accountId} myPerms={myPerms} />}
      </div>
    </>
  );
}
